(() => {
  const CUSTOMIZATION_PRODUCT_ID = 8824242765960;
  const FEE_PRODUCT_ID = 8825746653320;
  const ELIGIBLE_PRODUCT_TYPE = "Customizable Apparel";
  const CUSTOMIZATION_ITEM_PRODUCT_TYPE = "customization-item";
  const CUSTOMIZATION_FEE_PRODUCT_TYPE = "customization-fee";
  const BULK_PAGE_URL = "/pages/bulk-screenprint";

  const BULK_DRAFT_KEY = "tb_bulk_order_draft_v1";
  const BULK_DRAFT_VERSION = 1;
  const BULK_DRAFT_TTL_MS = 1000 * 60 * 60 * 24 * 7;
  const BUNDLE_PROP_KEY = "_tb_bundle_id";
  const TS_PROP_KEY = "_tb_ts";

  const UPLOAD_KEYS = ["front_img", "back_img", "sleeve_l_img", "sleeve_r_img"];

  const FEE_VARIANT_ID = {
    1: {
      "1-2 Color": 44767522553992,
      "Full Color 24-71": 44767373295752,
      "Full Color 72-143": 44767373328520,
      "Full Color 144-287": 44767373361288,
      "Full Color 288+": 44767373394056,
    },
    2: {
      "1-2 Color": 44767522586760,
      "Full Color 24-71": 44767166595208,
      "Full Color 72-143": 44767373426824,
      "Full Color 144-287": 44767373459592,
      "Full Color 288+": 44767373492360,
    },
    3: {
      "1-2 Color": 44767522619528,
      "Full Color 24-71": 44767373525128,
      "Full Color 72-143": 44767373557896,
      "Full Color 144-287": 44767373590664,
      "Full Color 288+": 44767373623432,
    },
    4: {
      "1-2 Color": 44767522652296,
      "Full Color 24-71": 44767373656200,
      "Full Color 72-143": 44767373688968,
      "Full Color 144-287": 44767373721736,
      "Full Color 288+": 44767373754504,
    },
  };

  let TB_LOCK = false;
  let TB_DRAFT_BOOTSTRAP = null;

  function safeLocalStorageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (_) {
      return null;
    }
  }

  function safeLocalStorageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch (_) {
      return false;
    }
  }

  function safeLocalStorageRemove(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (_) {}
  }

  function normalizeDraftShape(draft) {
    if (!draft || draft.version !== BULK_DRAFT_VERSION) return null;
    if (!Array.isArray(draft.items) || !draft.items.length) return null;

    const updatedAt = Number(draft.updatedAt || 0);
    if (!updatedAt || Date.now() - updatedAt > BULK_DRAFT_TTL_MS) return null;

    const items = draft.items
      .map((item) => ({
        variantId: String(item.variantId || ""),
        productId: String(item.productId || ""),
        productTitle: String(item.productTitle || "").trim(),
        size: String(item.size || "").trim(),
        color: String(item.color || "").trim(),
        qty: Math.max(0, parseInt(item.qty, 10) || 0),
        unitPriceCents: Math.max(0, parseInt(item.unitPriceCents, 10) || 0),
      }))
      .filter((item) => item.variantId && item.qty > 0);

    if (!items.length) return null;

    const totalUnits =
      Number(draft.totalUnits) > 0
        ? Number(draft.totalUnits)
        : items.reduce((sum, item) => sum + item.qty, 0);

    const baseTotalCents =
      Number(draft.baseTotalCents) >= 0
        ? Number(draft.baseTotalCents)
        : items.reduce((sum, item) => sum + item.unitPriceCents * item.qty, 0);

    return {
      version: BULK_DRAFT_VERSION,
      bundleId: String(draft.bundleId || "").trim(),
      updatedAt,
      sourcePath: String(draft.sourcePath || "").trim(),
      totalUnits,
      baseTotalCents,
      items,
      legacyNoBundle: Boolean(draft.legacyNoBundle),
    };
  }

  function loadBulkDraft() {
    const raw = safeLocalStorageGet(BULK_DRAFT_KEY);
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      const normalized = normalizeDraftShape(parsed);
      if (!normalized) {
        safeLocalStorageRemove(BULK_DRAFT_KEY);
        return null;
      }
      return normalized;
    } catch (_) {
      safeLocalStorageRemove(BULK_DRAFT_KEY);
      return null;
    }
  }

  function saveBulkDraft(draft) {
    const normalized = normalizeDraftShape({
      ...draft,
      version: BULK_DRAFT_VERSION,
      updatedAt: Date.now(),
    });

    if (!normalized) return null;
    if (!safeLocalStorageSet(BULK_DRAFT_KEY, JSON.stringify(normalized))) {
      throw new Error("Unable to persist bulk order draft");
    }
    return normalized;
  }

  function clearBulkDraft() {
    safeLocalStorageRemove(BULK_DRAFT_KEY);
    TB_DRAFT_BOOTSTRAP = null;
  }

  async function fetchCart() {
    const res = await fetch("/cart.js", { credentials: "same-origin", cache: "no-store" });
    if (!res.ok) throw new Error("Failed to fetch /cart.js");
    return res.json();
  }

  function parseTs(props) {
    const value = props && props[TS_PROP_KEY];
    const n = value ? Number(String(value)) : 0;
    return Number.isFinite(n) ? n : 0;
  }

  function normalizeProductType(item) {
    return String(item && item.product_type ? item.product_type : "").trim().toLowerCase();
  }

  function isCustomizationItemType(item) {
    return normalizeProductType(item) === CUSTOMIZATION_ITEM_PRODUCT_TYPE;
  }

  function isCustomizationFeeType(item) {
    return normalizeProductType(item) === CUSTOMIZATION_FEE_PRODUCT_TYPE;
  }

  function pickNewestByTs(items) {
    let best = null;
    let bestTs = -1;
    let bestIdx = -1;

    items.forEach((item, idx) => {
      const ts = parseTs(item.properties);
      if (ts > bestTs || (ts === bestTs && idx > bestIdx)) {
        best = item;
        bestTs = ts;
        bestIdx = idx;
      }
    });

    return best;
  }

  function deriveUnitPriceCents(cartItem) {
    if (!cartItem) return 0;

    const rawCandidates = [
      cartItem.price,
      cartItem.final_price && cartItem.quantity ? Math.round(cartItem.final_price / cartItem.quantity) : 0,
      cartItem.original_price,
    ];

    for (const candidate of rawCandidates) {
      const cents = Math.max(0, parseInt(candidate, 10) || 0);
      if (cents > 0) return cents;
    }

    return 0;
  }

  function buildDraftFromCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;

    const customizationItems = cart.items.filter((item) => isCustomizationItemType(item));
    const customization = customizationItems.length ? pickNewestByTs(customizationItems) : null;
    if (!customization) return null;

    const existingBundleId = String(customization.properties?.[BUNDLE_PROP_KEY] || "").trim();
    const legacyNoBundle = !existingBundleId;
    const bundleId = existingBundleId || `tb-legacy-${parseTs(customization.properties) || Date.now()}`;

    const apparelItems = cart.items.filter((item) => {
      if (String(item.product_type || "").trim() !== ELIGIBLE_PRODUCT_TYPE) return false;
      if (legacyNoBundle) return true;
      return String(item.properties?.[BUNDLE_PROP_KEY] || "").trim() === bundleId;
    });

    if (!apparelItems.length) return null;

    const items = apparelItems
      .map((item) => ({
        variantId: String(item.variant_id || ""),
        productId: String(item.product_id || ""),
        productTitle: String(item.product_title || item.title || "").trim(),
        size: "",
        color: "",
        qty: Math.max(0, parseInt(item.quantity, 10) || 0),
        unitPriceCents: deriveUnitPriceCents(item),
      }))
      .filter((item) => item.variantId && item.qty > 0);

    if (!items.length) return null;

    return {
      version: BULK_DRAFT_VERSION,
      bundleId,
      updatedAt: Date.now(),
      sourcePath: location.pathname,
      totalUnits: items.reduce((sum, item) => sum + item.qty, 0),
      baseTotalCents: items.reduce((sum, item) => sum + item.unitPriceCents * item.qty, 0),
      items,
      legacyNoBundle,
    };
  }

  async function resolveBulkDraft() {
    const storedDraft = loadBulkDraft();
    if (storedDraft && storedDraft.sourcePath === BULK_PAGE_URL) {
      return storedDraft;
    }

    // The staged bulk draft is the source of truth for the customization flow.
    // Do not rebuild draft state from cart contents here, since the bulk qty UI
    // is now decoupled from Easy Wholesale's internal ordering state.
    if (storedDraft) return storedDraft;
    return null;
  }

  function getFullColorTierLabel(eligibleQty) {
    if (eligibleQty >= 288) return "Full Color 288+";
    if (eligibleQty >= 144) return "Full Color 144-287";
    if (eligibleQty >= 72) return "Full Color 72-143";
    return "Full Color 24-71";
  }

  function getSubmitButton() {
    return (
      document.querySelector("#ProductSubmitButton-template--18236989341832__main") ||
      document.querySelector("#ProductSubmitButton-template--19664045408392__main") ||
      document.querySelector('button[name="add"][type="submit"]') ||
      document.querySelector('form[action*="/cart/add"] button[type="submit"]')
    );
  }

  function getAddToCartForm() {
    const btn = getSubmitButton();
    const byBtn = btn ? btn.closest("form") : null;
    if (byBtn) return byBtn;
    const forms = Array.from(document.querySelectorAll('form[action*="/cart/add"]'));
    return forms[0] || null;
  }

  function getBackToOrderButton() {
    return document.querySelector(".tb-back-order-btn[href='/pages/bulk-screenprint']");
  }

  function getLpoPropertyElements() {
    const wrapper =
      document.querySelector(".cl-po--wrapper") ||
      document.querySelector('[id^="clai"]') ||
      document;

    return Array.from(
      wrapper.querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]')
    );
  }

  function getUploadCountRawFromProps(props) {
    let count = 0;
    for (const key of UPLOAD_KEYS) {
      const value = props[key];
      if (typeof value === "string" && value.trim()) count += 1;
    }
    return Math.min(4, Math.max(0, count));
  }

  function clampUploadsForFee(rawCount) {
    return Math.min(4, Math.max(1, Number(rawCount) || 0));
  }

  function readSelectedColor(props) {
    const raw = String(props["color-select"] || "").toLowerCase().trim();
    if (raw === "full-color" || raw === "full color" || raw === "full_color") return "full-color";
    return raw;
  }

  function parsePropsFromElements(propEls) {
    const props = {};
    for (const el of propEls) {
      if (!el || !el.name || !el.name.startsWith("properties[")) continue;

      const type = (el.type || "").toLowerCase();
      if ((type === "radio" || type === "checkbox") && !el.checked) continue;

      const m = el.name.match(/^properties\[(.+)\]$/);
      if (!m) continue;
      props[m[1]] = String(el.value ?? "").trim();
    }
    return props;
  }

  function tbSetCartPending(message) {
    try {
      if (
        window.TB_CART_LOCK &&
        window.TB_CART_LOCK.overlay &&
        typeof window.TB_CART_LOCK.overlay.setPending === "function"
      ) {
        window.TB_CART_LOCK.overlay.setPending(message || "Updating Cart");
        return;
      }
    } catch (_) {}

    try {
      sessionStorage.setItem("tb_cart_op_pending", "1");
      sessionStorage.setItem("tb_cart_op_started_at", String(Date.now()));
      if (message) sessionStorage.setItem("tb_cart_op_message", String(message));
    } catch (_) {}
  }

  function showInlineMessage(id, button, message) {
    try {
      let host = button ? button.closest("form") : null;
      host = host || document.querySelector("main") || document.body;

      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement("div");
        el.id = id;
        el.style.marginTop = "10px";
        el.style.fontSize = "14px";
        el.style.lineHeight = "1.3";
        el.style.color = "#b00020";
        el.style.fontWeight = "600";
        if (button && button.parentElement) {
          button.parentElement.appendChild(el);
        } else {
          host.appendChild(el);
        }
      }
      el.textContent = message;
      setTimeout(() => {
        try {
          el.remove();
        } catch (_) {}
      }, 6000);
    } catch (_) {
      alert(message);
    }
  }

  function showUploadRequiredError(btn) {
    showInlineMessage(
      "tb-upload-required",
      btn,
      "Please upload at least one image before adding to cart."
    );
  }

  function showBulkDraftRequiredError(btn) {
    showInlineMessage(
      "tb-bulk-draft-required",
      btn,
      "Please complete Step 1 on the bulk order page before continuing."
    );
  }

  function shouldIncludeProp(key, props) {
    if (key === "front_img_location") return !!(props.front_img && String(props.front_img).trim());
    if (key === "back_img_location") return !!(props.back_img && String(props.back_img).trim());
    if (key === "sleeve_l_img_location") return !!(props.sleeve_l_img && String(props.sleeve_l_img).trim());
    if (key === "sleeve_r_img_location") return !!(props.sleeve_r_img && String(props.sleeve_r_img).trim());
    return true;
  }

  async function cartAddFormData(form, propEls, tbTs, propsSnapshot, bundleId) {
    const fd = new FormData(form);

    for (const el of propEls) {
      if (!el || !el.name || !el.name.startsWith("properties[")) continue;

      const type = (el.type || "").toLowerCase();
      if ((type === "radio" || type === "checkbox") && !el.checked) continue;

      const m = el.name.match(/^properties\[(.+)\]$/);
      const key = m ? m[1] : null;
      const val = String(el.value ?? "").trim();
      if (!key) continue;
      if (propsSnapshot && !shouldIncludeProp(key, propsSnapshot)) continue;
      fd.set(el.name, val);
    }

    if (propsSnapshot) {
      if (!shouldIncludeProp("front_img_location", propsSnapshot)) fd.delete("properties[front_img_location]");
      if (!shouldIncludeProp("back_img_location", propsSnapshot)) fd.delete("properties[back_img_location]");
      if (!shouldIncludeProp("sleeve_l_img_location", propsSnapshot)) fd.delete("properties[sleeve_l_img_location]");
      if (!shouldIncludeProp("sleeve_r_img_location", propsSnapshot)) fd.delete("properties[sleeve_r_img_location]");
    }

    fd.set("properties[_tb_customization]", "screenprint");
    fd.set(`properties[${TS_PROP_KEY}]`, String(tbTs || ""));
    fd.set(`properties[${BUNDLE_PROP_KEY}]`, String(bundleId || ""));

    const res = await fetch("/cart/add.js", {
      method: "POST",
      body: fd,
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`Failed adding customization product (/cart/add.js): ${text}`);
    }

    return res.json();
  }

  async function cartChangeLineItem(lineKey, qty) {
    const body = new URLSearchParams();
    body.set("id", lineKey);
    body.set("quantity", String(qty));

    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
      },
      body,
      credentials: "same-origin",
    });

    if (!res.ok) throw new Error("Failed POST /cart/change.js");
    return res.json();
  }

  async function cartAddItems(items) {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ items }),
      credentials: "same-origin",
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`Failed POST /cart/add.js (bulk apparel add): ${text}`);
    }

    return res.json();
  }

  async function cartAddVariant(variantId, qty, properties = {}) {
    return cartAddItems([{ id: variantId, quantity: qty, properties }]);
  }

  function buildApparelPayload(draft, tbTs) {
    return draft.items.map((item) => ({
      id: Number(item.variantId),
      quantity: Number(item.qty),
      properties: {
        [BUNDLE_PROP_KEY]: draft.bundleId,
        [TS_PROP_KEY]: String(tbTs),
      },
    }));
  }

  function matchesBundleLine(item, draft) {
    const itemBundleId = String(item?.properties?.[BUNDLE_PROP_KEY] || "").trim();
    if (itemBundleId && itemBundleId === draft.bundleId) return true;

    if (!draft.legacyNoBundle) return false;

    const productType = String(item?.product_type || "").trim();
    return (
      productType === ELIGIBLE_PRODUCT_TYPE ||
      isCustomizationItemType(item) ||
      isCustomizationFeeType(item)
    );
  }

  async function removeExistingBundleLines(draft) {
    const cart = await fetchCart();
    const bundleLines = cart.items.filter((item) => matchesBundleLine(item, draft));

    for (const item of bundleLines) {
      await cartChangeLineItem(item.key, 0);
    }
  }

  async function runAddFlow() {
    if (TB_LOCK) return;
    TB_LOCK = true;

    const tbTs = String(Date.now());
    const btn = getSubmitButton();
    const form = getAddToCartForm();
    const propEls = getLpoPropertyElements();

    try {
      if (!form) throw new Error("No product form found");

      try {
        window.TB_CART_LOCK?.overlay?.show("Updating Cart");
      } catch (_) {}

      if (btn) btn.disabled = true;

      const props = parsePropsFromElements(propEls);
      const uploadCountRaw = getUploadCountRawFromProps(props);
      if (uploadCountRaw === 0) {
        TB_LOCK = false;
        if (btn) btn.disabled = false;
        try {
          window.TB_CART_LOCK?.overlay?.hide();
        } catch (_) {}
        showUploadRequiredError(btn);
        return;
      }

      const draft = await resolveBulkDraft();
      if (!draft || !draft.items.length) {
        TB_LOCK = false;
        if (btn) btn.disabled = false;
        try {
          window.TB_CART_LOCK?.overlay?.hide();
        } catch (_) {}
        showBulkDraftRequiredError(btn);
        return;
      }

      const uploadCount = clampUploadsForFee(uploadCountRaw);
      const rawColor = readSelectedColor(props);
      const isFullColor = rawColor === "full-color";
      const eligibleQty = draft.totalUnits;

      await removeExistingBundleLines(draft);
      await cartAddItems(buildApparelPayload(draft, tbTs));
      await cartAddFormData(form, propEls, tbTs, props, draft.bundleId);

      if (eligibleQty > 0) {
        const tierLabel = isFullColor ? getFullColorTierLabel(eligibleQty) : "1-2 Color";
        const desiredVariantId = (FEE_VARIANT_ID[uploadCount] || {})[tierLabel];

        if (!desiredVariantId) {
          throw new Error(`No fee variant match for uploads=${uploadCount}, tier=${tierLabel}`);
        }

        await cartAddVariant(desiredVariantId, eligibleQty, {
          _tb_fee: "screenprint",
          [TS_PROP_KEY]: String(tbTs),
          [BUNDLE_PROP_KEY]: draft.bundleId,
        });
      }

      clearBulkDraft();
      safeLocalStorageRemove(BULK_DRAFT_KEY);
      tbSetCartPending("Updating Cart");
      window.location.href = "/cart";
    } catch (err) {
      console.error("[TB] Screenprint customization flow error:", err);
      TB_LOCK = false;
      if (btn) btn.disabled = false;
      try {
        window.TB_CART_LOCK?.overlay?.hide();
      } catch (_) {}
    }
  }

  async function prepareDraftForBackToOrder() {
    try {
      await resolveBulkDraft();
    } catch (error) {
      console.error("[TB] Failed preparing draft for back-to-order:", error);
    }
  }

  function bindBackToOrder() {
    const backBtn = getBackToOrderButton();
    if (!backBtn || backBtn.dataset.tbBound === "1") return;
    backBtn.dataset.tbBound = "1";

    backBtn.addEventListener("click", async (event) => {
      event.preventDefault();
      await prepareDraftForBackToOrder();
      window.location.assign(BULK_PAGE_URL);
    });
  }

  function init() {
    const btn = getSubmitButton();
    if (btn && !btn.__tbBound) {
      btn.__tbBound = true;
      btn.addEventListener(
        "click",
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          runAddFlow();
        },
        true
      );
    }

    bindBackToOrder();

    if (!TB_DRAFT_BOOTSTRAP) {
      TB_DRAFT_BOOTSTRAP = resolveBulkDraft().catch((error) => {
        console.error("[TB] Failed resolving bulk draft:", error);
        return null;
      });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  setTimeout(init, 600);
  setTimeout(init, 1200);
})();
